import { useEffect, useRef } from 'react';
import { CAD_FILL_LAYER_ID, queryCadOverlayAtPoint } from './useCadParcelsLayer';

/**
 * Arrastre del loteo CAD sobre el mapa país. Pan del mapa se apaga mientras
 * el cursor está sobre el sticker.
 *
 * @param {{
 *   mapInstanceRef: object,
 *   enabled?: boolean,
 *   onTranslate?: (delta: { dlng: number, dlat: number }) => void,
 * }} options
 */
export function useCadGeorefStickerLayer({
  mapInstanceRef,
  enabled = false,
  onTranslate,
}) {
  const onTranslateRef = useRef(onTranslate);
  onTranslateRef.current = onTranslate;
  const dragRef = useRef(null);

  useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !enabled) return undefined;

    const canvas = map.getCanvas();
    const stopDrag = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      try { map.dragPan.enable(); } catch (_) { /* noop */ }
      try { canvas.style.cursor = 'grab'; } catch (_) { /* noop */ }
    };

    const hitsSticker = (point) => {
      if (!point) return false;
      return queryCadOverlayAtPoint(map, point, [CAD_FILL_LAYER_ID]).length > 0;
    };

    const onMouseDown = (event) => {
      if (!hitsSticker(event.point)) return;
      dragRef.current = {
        lng: event.lngLat.lng,
        lat: event.lngLat.lat,
      };
      try { map.dragPan.disable(); } catch (_) { /* noop */ }
      try { canvas.style.cursor = 'grabbing'; } catch (_) { /* noop */ }
      event.preventDefault();
    };

    const onMouseMove = (event) => {
      const drag = dragRef.current;
      if (!drag) {
        try {
          canvas.style.cursor = hitsSticker(event.point) ? 'grab' : '';
        } catch (_) { /* noop */ }
        return;
      }
      const dlng = event.lngLat.lng - drag.lng;
      const dlat = event.lngLat.lat - drag.lat;
      drag.lng = event.lngLat.lng;
      drag.lat = event.lngLat.lat;
      if (dlng || dlat) onTranslateRef.current?.({ dlng, dlat });
    };

    const onMouseUp = () => stopDrag();
    const onMouseOut = () => stopDrag();

    map.on('mousedown', onMouseDown);
    map.on('mousemove', onMouseMove);
    map.on('mouseup', onMouseUp);
    map.on('mouseout', onMouseOut);

    try { canvas.style.cursor = 'grab'; } catch (_) { /* noop */ }

    return () => {
      stopDrag();
      map.off('mousedown', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
      map.off('mouseout', onMouseOut);
      try { canvas.style.cursor = ''; } catch (_) { /* noop */ }
    };
  }, [enabled, mapInstanceRef]);
}
